import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { CircleUserRound, Mic, Pin, Search, UsersRound, Video, VolumeX } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, ChatSidebarSkeleton, ChatSkeleton } from '@/components';
import { useERSContext } from '@/context';
import { constants } from '@/lib/constants';
import { cn, getPresenceColor } from '@/lib/utils';
import { useAIProvider } from '@/hooks/useAIProvider';

dayjs.extend(relativeTime);

export const ChatSidebar = ({
  chatList = [],
  onChatSelect,
  selectedChat,
  pinnedChats = new Set(),
  mutedChats = new Set(),
  onTogglePin,
  onToggleMute,
  isChatMuted = () => false,
  incomingVideoStream = {},
  unreadInfo = {},
  userSpeaking = {},
}) => {
  const { t, i18n } = useTranslation();
  const { users, selectedChatId, registerScrollToChat, chatHistory } = useERSContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [isTranslating, setIsTranslating] = useState(false);

  const { callAPI } = useAIProvider({
    model: 'gpt-4o-mini',
    temperature: 0.3,
  });

  const [translations, setTranslations] = useState({});

  const chatListRef = useRef(null);
  const scrollTimeoutRef = useRef(null);

  const safePinnedChats = pinnedChats || new Set();
  const safeMutedChats = mutedChats || new Set();

  // Split display name into serial ID and incident type
  const splitDisplayName = displayName => {
    if (!displayName) return { serialId: '', incidentType: '' };

    const parts = displayName.split(' - ');
    if (parts.length === 2) {
      return { serialId: parts[0], incidentType: parts[1] };
    }
    return { serialId: '', incidentType: displayName };
  };

  // Get translated incident type from translation files
  const getTranslatedIncidentType = incidentType => {
    if (!incidentType) return '';

    // Get all incident types from translation
    const incidentTypes = t('INCIDENT_TYPES', { returnObjects: true });
    if (!Array.isArray(incidentTypes)) return incidentType;

    // Find the matching incident type and return its translated label
    const matchedType = incidentTypes.find(type => type.value === incidentType);
    return matchedType?.label || incidentType;
  };

  // Get translated display name using translation files
  const getTranslatedDisplayName = chat => {
    if (!chat.displayName) return '';

    const { serialId, incidentType } = splitDisplayName(chat.displayName);

    if (!incidentType) return chat.displayName;

    const translatedIncidentType = getTranslatedIncidentType(incidentType);

    return serialId ? `${serialId} - ${translatedIncidentType}` : translatedIncidentType;
  };

  // Batch translation only for last messages

  useEffect(() => {
    translateLastMessages();
  }, [i18n.language, chatHistory]);

  const translateLastMessages = async () => {
    if (!chatList || chatList.length === 0) {
      setIsTranslating(false);
      return;
    }

    const translationItems = [];

    chatList.forEach(chat => {
      // Only translate last message body (not display names)
      const lastMessage = getLastMessage(chat.id);
      if (lastMessage?.body && lastMessage.body.trim()) {
        const messageCacheKey = `sidebar-message-${chat.id}-${lastMessage.body}-${i18n.language}`;
        // Remove the duplicate check - translate every message even if text is the same
        if (!translations[messageCacheKey]) {
          translationItems.push({
            text: lastMessage.body,
            cacheKey: messageCacheKey,
            chatId: chat.id, // Add chatId to track which chat this belongs to
          });
        }
      }
    });

    // If no translations needed, skip API call
    if (translationItems.length === 0) {
      setIsTranslating(false);
      return;
    }

    // Single API call for all message translations
    setIsTranslating(true);
    try {
      const texts = translationItems.map(item => item.text);
      const prompt = `Translate the following texts into ${i18n.language} without adding any extra context or punctuation. Provide only the translations in the same order, separated by "|||":\n\n${texts.map(text => text).join('\n')}`;

      const translated = await callAPI([{ role: 'user', content: prompt }]);

      const translationsArray = translated.split('|||').map(t => {
        let cleaned = t.trim();
        // Remove surrounding quotes
        if ((cleaned.startsWith('"') && cleaned.endsWith('"')) || (cleaned.startsWith("'") && cleaned.endsWith("'"))) {
          cleaned = cleaned.slice(1, -1);
        }
        return cleaned.replace(/^["']+|["']+$/g, '').trim();
      });

      const newTranslations = {};
      translationItems.forEach((item, index) => {
        if (translationsArray[index]) {
          newTranslations[item.cacheKey] = translationsArray[index];
        }
      });

      setTranslations(prev => ({
        ...prev,
        ...newTranslations,
      }));
    } catch (err) {
      console.error('Chat sidebar message translation failed:', err);
    } finally {
      setIsTranslating(false);
    }
  };

  const normalizeChatId = chatId => {
    if (!chatId) return '';
    return chatId.split('@')[0];
  };

  const scrollToSelectedChat = useCallback(() => {
    if (!selectedChatId || !chatListRef.current) return;

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);

    scrollTimeoutRef.current = setTimeout(() => {
      try {
        const container = chatListRef.current;
        if (!container) return;

        const chatElements = container.querySelectorAll('[data-chat-id]');
        let selectedElement = null;

        for (const element of chatElements) {
          const elementChatId = element.getAttribute('data-chat-id');
          const normalizedElementId = normalizeChatId(elementChatId);
          const normalizedSelectedId = normalizeChatId(selectedChatId);

          if (normalizedElementId === normalizedSelectedId) {
            selectedElement = element;
            break;
          }
        }

        if (selectedElement) {
          const containerRect = container.getBoundingClientRect();
          const elementRect = selectedElement.getBoundingClientRect();
          const offsetTop = elementRect.top - containerRect.top + container.scrollTop;

          container.scrollTo({
            top: offsetTop - 10,
            behavior: 'smooth',
          });
        }
      } catch (error) {
        console.warn('Error scrolling to selected chat:', error);
      }
    }, 300);
  }, [selectedChatId]);

  useEffect(() => {
    registerScrollToChat(scrollToSelectedChat);
  }, [registerScrollToChat, scrollToSelectedChat]);

  useEffect(() => {
    scrollToSelectedChat();

    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [selectedChatId, scrollToSelectedChat]);

  const getLastMessage = chatId => {
    const history = chatHistory[chatId]?.messages;
    if (!history || history.length === 0) return null;
    return history[history.length - 1];
  };

  const formatMessageTime = timestamp => {
    if (!timestamp) return '';
    const messageDate = dayjs(timestamp);
    const today = dayjs().startOf('day');
    const yesterday = today.subtract(1, 'day');

    if (messageDate.isSame(today, 'day')) {
      return messageDate.format('h:mm a'); // Keep time format as-is
    }

    if (messageDate.isSame(yesterday, 'day')) {
      return t('yesterday'); // Translate "Yesterday"
    }

    if (messageDate.isSame(today, 'week')) {
      // Get the day name and translate it
      const dayName = messageDate.format('dddd'); // Gets full day name (Sunday, Monday, etc.)
      const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayIndex = days.indexOf(dayName.toLowerCase());

      if (dayIndex !== -1) {
        return t(`days.${dayIndex}`); // Translate day name
      }

      return dayName; // Fallback to original if not found
    }

    return messageDate.format('M/D/YY'); // Keep date format as-is
  };
  const handlePinClick = (e, chatId) => {
    e.stopPropagation();
    onTogglePin?.(chatId);
  };

  const handleMuteClick = (e, chatId) => {
    e.stopPropagation();
    onToggleMute?.(chatId);
  };

  const isUserSpeaking = chatId => userSpeaking && userSpeaking[chatId] === true;

  const hasIncomingVideoStream = chatId => {
    return incomingVideoStream.some(stream => {
      return stream.contactId === chatId && !stream.completed;
    });
  };

  const getUnreadCount = chatId => unreadInfo[chatId]?.count || 0;

  // Helper function to get translated last message
  const getTranslatedLastMessage = chat => {
    const lastMessage = getLastMessage(chat.id);
    if (!lastMessage?.body) return '';
    const cacheKey = `sidebar-message-${chat.id}-${lastMessage.body}-${i18n.language}`;
    return translations[cacheKey] || lastMessage.body;
  };

  // Check if a specific chat has translations ready
  const isChatTranslated = chat => {
    if (!chat.displayName) return true;

    const displayNameCacheKey = `sidebar-displayName-${chat.id}-${chat.displayName}-${i18n.language}`;
    const lastMessage = getLastMessage(chat.id);
    const messageCacheKey = lastMessage?.body
      ? `sidebar-message-${chat.id}-${lastMessage.body}-${i18n.language}`
      : null;
    return !isTranslating && (translations[displayNameCacheKey] !== undefined || !chat.displayName.trim());
  };

  const filteredChats = useMemo(() => {
    if (!searchTerm.trim()) return chatList;

    // Search in both original and translated display names
    return chatList.filter(chat => {
      const originalName = chat.displayName?.toLowerCase() || '';
      const translatedName = getTranslatedDisplayName(chat)?.toLowerCase() || '';

      return originalName.includes(searchTerm.toLowerCase()) || translatedName.includes(searchTerm.toLowerCase());
    });
  }, [searchTerm, chatList, getTranslatedDisplayName]);

  const handleUsersPresenece = id => {
    const usersObj = users?.filter(user => user?.id == id);
    return getPresenceColor(usersObj[0]?.presence);
  };

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      {/* Search bar */}
      <div className="p-2 h-16 items-center flex w-full border-b border-b-border flex-shrink-0 z-10">
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted" size={18} />
          <input
            type="text"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            placeholder={t('CHAT_PANEL.SEARCH_PLACEHOLDER')}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg bg"
          />
        </div>
      </div>

      {/* Chat list */}
      <div ref={chatListRef} className="flex-1 overflow-y-auto overflow-x-hidden">
        {filteredChats.length > 0 ? (
          filteredChats.map(chat => {
            const normalizedChatId = normalizeChatId(chat.id);
            const normalizedSelectedId = normalizeChatId(selectedChatId);
            const isSelected = selectedChat?.id === chat.id;

            const lastMessage = getLastMessage(chat.id);
            const isPinned = safePinnedChats.has(chat.id);
            const isMuted = isChatMuted(chat.id);
            const speaking = isUserSpeaking(chat.id);
            const hasIncomingVideo = hasIncomingVideoStream(chat.id);
            const unreadCount = getUnreadCount(chat.id);

            const translatedDisplayName = getTranslatedDisplayName(chat);
            const translatedLastMessage = getTranslatedLastMessage(chat);

            // Show skeleton for individual chat if still translating this specific chat
            const showChatSkeleton = isTranslating && !isChatTranslated(chat);

            return (
              <div
                key={chat.id}
                data-chat-id={chat.id}
                onClick={() => onChatSelect?.(chat)}
                className={cn(
                  'p-2 h-14 cursor-pointer flex justify-between items-center border-b hover:bg-subtle transition-all duration-200 relative',
                  isSelected && 'bg-subtle border-l-4 border-l-primary shadow-sm border border-interactive-active',
                  isMuted && 'opacity-70',
                  speaking && 'bg-primary/70 animate-pulse border-l-4 border-l-success-strong'
                )}
              >
                <div className="flex items-center justify-between w-full gap-2">
                  {/* Left side - Avatar and chat info */}
                  <div className="flex items-center gap-2 min-w-0 max-w-72 flex-1">
                    {chat.type === 'group' ? (
                      <div className="w-8 h-8 rounded-full flex justify-center items-center bg-elevated relative flex-shrink-0">
                        <UsersRound size={20} absoluteStrokeWidth className="text-icon" />
                      </div>
                    ) : (
                      <div className="relative w-8 h-8 flex justify-center items-center flex-shrink-0">
                        <CircleUserRound size={24} absoluteStrokeWidth className="text-icon" />
                        <span
                          className={cn(
                            'absolute bottom-0.5 right-0.5 block h-2.5 w-2.5 rounded-full border-2 border-onColor',
                            handleUsersPresenece(chat?.id)
                          )}
                        />
                      </div>
                    )}

                    <div className="flex flex-col justify-center min-w-0 flex-1">
                      <p className="text-sm font-normal truncate max-w-72">{translatedDisplayName}</p>
                      <div
                        className={cn(
                          'text-xs font-normal truncate',
                          speaking || hasIncomingVideo ? 'text-primary font-medium' : 'text-placeholder'
                        )}
                      >
                        {hasIncomingVideo ? (
                          <span className="flex items-center gap-1 text-interactive">
                            <Video size={14} />
                            {t('CHAT_PANEL.INCOMING_VIDEO_CALL')}
                          </span>
                        ) : speaking ? (
                          <span className="flex items-center gap-1 text-interactive">
                            <Mic size={14} />
                            {translatedDisplayName} {t('CHAT_PANEL.IS_SPEAKING')}
                          </span>
                        ) : lastMessage ? (
                          showChatSkeleton ? (
                            <div className="flex items-center gap-1">
                              <div className="w-4 h-2 bg-muted rounded animate-pulse"></div>
                              <div className="w-16 h-2 bg-muted rounded animate-pulse"></div>
                              <div className="w-8 h-2 bg-muted rounded animate-pulse"></div>
                            </div>
                          ) : (
                            translatedLastMessage
                          )
                        ) : (
                          t('CHAT_PANEL.NO_MESSAGES_YET')
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right side - Time, pin, mute, badge */}
                  <div className="flex flex-col items-end justify-center flex-shrink-0">
                    <span className="text-xs text-placeholder font-normal whitespace-nowrap">
                      {lastMessage ? formatMessageTime(lastMessage.sendingDate || lastMessage.receivedDate) : ''}
                    </span>
                    <div className="flex flex-row gap-1 items-center mt-1">
                      {isPinned && (
                        <Pin
                          size={16}
                          className="cursor-pointer text-muted flex-shrink-0"
                          onClick={e => handlePinClick(e, chat.id)}
                        />
                      )}
                      {isMuted && (
                        <VolumeX
                          size={16}
                          className="cursor-pointer text-muted flex-shrink-0"
                          onClick={e => handleMuteClick(e, chat.id)}
                        />
                      )}
                      {unreadCount > 0 && <Badge className="flex-shrink-0">{unreadCount}</Badge>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="p-4 text-center text-sm text-placeholder">{t('CHAT_PANEL.NO_CHAT_FOUND')}</div>
        )}
      </div>
    </div>
  );
};
